import React from "react";
import { css } from "@emotion/css";

import type { OfferCard, SessionOffer } from "../../types/state";

/**
 * OfferCards — what to do next, as cards.
 *
 * The dealer decided; this draws.  Three things it is careful about:
 *
 * - **The hero reads as the answer, not as one of three.**  A learner
 *   who wants to be told what to do should be able to press once without
 *   comparing anything.
 * - **Every card says why.**  The dealer's reasons are already words a
 *   learner can read ("you were sure about this one and it did not
 *   hold"), and showing them is what makes the recommendation a
 *   suggestion rather than a slot machine.
 * - **There is always a way out.**  "Other waters" goes to the atlas,
 *   because being boxed into three cards is how a recommendation becomes
 *   a cage.
 */

const listStyle = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
`;

const cardStyle = css`
  display: block;
  width: 100%;
  text-align: left;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid var(--color-border-subtle);
  background: var(--color-bg-subtle);
  color: var(--color-text);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover:not(:disabled) {
    border-color: var(--color-border);
    background: var(--color-bg-elevated);
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const heroStyle = css`
  border-color: var(--color-primary);
  background: var(--color-primary-muted);
`;

const anteStyle = css`
  border-style: dashed;
`;

const titleStyle = css`
  font-weight: 600;
  line-height: 1.35;
`;

const subtitleStyle = css`
  margin-top: 3px;
  font-size: 0.88em;
  color: var(--color-text-secondary);
  line-height: 1.45;
`;

const metaStyle = css`
  margin-top: 6px;
  font-size: 0.78em;
  color: var(--color-text-muted);
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
`;

const watersStyle = css`
  margin-top: 12px;
  font-size: 0.82em;
  color: var(--color-text-muted);
`;

const linkStyle = css`
  border: none;
  background: none;
  padding: 0;
  color: var(--color-primary);
  font: inherit;
  cursor: pointer;
  text-decoration: underline;
`;

export interface OfferCardsProps {
  offer: SessionOffer | null | undefined;
  /** Take a card.  The caller commits it and navigates. */
  onTake: (card: OfferCard) => void;
  /** Leave for the atlas — the way out of these three. */
  onOtherWaters?: () => void;
  /** A commit is in flight; every card is inert until it lands. */
  busy?: boolean;
}

export const OfferCards: React.FC<OfferCardsProps> = ({
  offer,
  onTake,
  onOtherWaters,
  busy = false,
}) => {
  const cards = offer?.cards ?? [];
  if (!cards.length) return null;

  return (
    <div data-testid="offer-cards">
      <div className={listStyle}>
        {cards.map((card) => {
          const emphasis =
            card.role === "hero" ? heroStyle : card.role === "ante" ? anteStyle : "";
          return (
            <button
              key={card.choiceId}
              type="button"
              disabled={busy}
              className={`${cardStyle}${emphasis ? ` ${emphasis}` : ""}`}
              onClick={() => onTake(card)}
              data-testid={`offer-card-${card.choiceId}`}
              data-role={card.role}
            >
              <div className={titleStyle}>{card.title}</div>
              {card.subtitle && <div className={subtitleStyle}>{card.subtitle}</div>}
              <div className={metaStyle}>
                {card.minutesWords && <span>{card.minutesWords}</span>}
                {(card.reasons ?? []).map((reason) => (
                  <span key={reason.code}>{reason.text}</span>
                ))}
              </div>
            </button>
          );
        })}
      </div>

      {onOtherWaters && (offer?.otherWaters?.length ?? 0) > 0 && (
        <div className={watersStyle} data-testid="offer-other-waters">
          Somewhere else?{" "}
          <button type="button" className={linkStyle} onClick={onOtherWaters}>
            open the atlas
          </button>
        </div>
      )}
    </div>
  );
};

export default OfferCards;
